/**
 * Building Stats Component
 * 
 * Displays production and stats for the building at the hovered tile position.
 */

import { getImagePath, getImageBasePath } from '../utils/imagePath.js';

import {
    WOOD, CUTTER, SAWMILL, CUTTER_OLD,
    STONE, QUARRY, STONEMASON, QUARRY_OLD,
    IRON, MINE, FOUNDRY, MINE_OLD,
    FOOD, FARM, MILL, FARM_OLD,
    GOLD, MANOR, BAZAAR, WHARF,
    CASA, CELLARIUM, CACHE,
    OPEN, LAKE, WALL, DEEP_WATER, WATER, TOWN_HALL,
    BARRACKS, CASTLE, CITY_GUARD_HOUSE, SHIPYARD,
    MOONGLOW, STABLES, TEMPLE, TRAINING_GROUND, WORKSHOP
} from '../core/Constants.js';
import {
    getGathererProduction,
    getFarmProduction,
    getEstateProduction,
    getCottageBonus,
    getRefineryBonus,
    getHarborBonus,
    getMarketBonus,
    getGranaryStorage,
    getVaultStorage,
    getKeepStorage,
    getKeepBuildingSlots,
    getBuildingCost,
    getTotalCostToLevel,
    formatBuildTime
} from '../core/BuildingData.js';
import { getTileImageKey } from '../utils/TileImageMap.js';
import { getMilbuildingSprite, MILBUILDINGS_SPRITE_MAP, getResbuildingSprite, RESBUILDINGS_SPRITE_MAP } from '../utils/SpriteSheet.js';

export class BuildingStats {
    constructor(containerElement, gameEngine) {
        this.container = containerElement;
        this.gameEngine = gameEngine;
        this.currentTile = null;
        
        this.render();
    }
    
    /**
     * Update for a specific tile
     */
    update(i, j) {
        this.currentTile = { i, j };
        this.render();
    }
    
    /**
     * Get building level for a tile
     * Uses basilicaLevel for TOWN_HALL, wallLevel for WALL, buildingLevel for all other buildings
     */
    getBuildingLevel(tile, i, j) {
        // Keep (Town Hall) uses basilicaLevel
        if (tile === TOWN_HALL) {
            if (this.gameEngine.basilicaLevel !== undefined && this.gameEngine.basilicaLevel !== null) {
                return this.gameEngine.basilicaLevel;
            }
            return 10; // Fallback to 10 if not set
        }
        
        // Wall uses wallLevel
        if (tile === WALL) {
            if (this.gameEngine.wallLevel !== undefined && this.gameEngine.wallLevel !== null) {
                return this.gameEngine.wallLevel;
            }
            return 10; // Fallback to 10 if not set
        }
        
        // All other buildings use buildingLevel
        if (this.gameEngine.buildingLevel !== undefined && this.gameEngine.buildingLevel !== null) {
            return this.gameEngine.buildingLevel;
        }
        // Fallback to 10 if not set
        return 10;
    }
    
    /**
     * Get building stats for a tile position
     */
    getBuildingStats(i, j) {
        if (!this.gameEngine || i < 0 || i >= 21 || j < 0 || j >= 21) {
            return null;
        }
        
        const tile = this.gameEngine.map[i][j];
        
        // Check if it's an empty tile
        if (tile === OPEN || tile === FOOD || tile === LAKE || tile === WOOD || tile === STONE || tile === IRON) {
            return null;
        }
        
        const level = this.getBuildingLevel(tile, i, j);
        
        const stats = {
            building: tile,
            level: level,
            production: {},
            bonuses: {},
            storage: null,
            costs: {},
            other: {}
        };
        
        // Calculate level-based production
        let baseProduction = 0;
        let resourceType = null;
        
        // Get base production based on building type and level
        if (tile === CUTTER || tile === CUTTER_OLD) {
            baseProduction = getGathererProduction(level);
            resourceType = WOOD;
        } else if (tile === QUARRY || tile === QUARRY_OLD) {
            baseProduction = getGathererProduction(level);
            resourceType = STONE;
        } else if (tile === MINE || tile === MINE_OLD) {
            baseProduction = getGathererProduction(level);
            resourceType = IRON;
        } else if (tile === FARM || tile === FARM_OLD) {
            baseProduction = getFarmProduction(level);
            resourceType = FOOD;
        } else if (tile === MANOR) {
            baseProduction = getEstateProduction(level);
        }
        
        // Calculate production with bonuses (matching GameEngine logic)
        if (resourceType !== null && baseProduction > 0) {
            let bonus = 0;
            let farmFieldNodes = [];
            
            // Special handling for farms: count farm field nodes (OPEN spots adjacent to farm)
            if (resourceType === FOOD) {
                // Count farm field nodes (OPEN spots adjacent to this farm)
                let farmFieldCount = 0;
                for (let di = -1; di <= 1; di++) {
                    for (let dj = -1; dj <= 1; dj++) {
                        if (di === 0 && dj === 0) continue; // Skip self
                        const ni = i + di;
                        const nj = j + dj;
                        if (ni >= 0 && ni < 21 && nj >= 0 && nj < 21) {
                            const neighborTile = this.gameEngine.map[ni][nj];
                            // Farm field node: OPEN spot (empty land) adjacent to farm
                            if (neighborTile === OPEN && !this.gameEngine.isLocked(ni, nj)) {
                                farmFieldCount++;
                                // Store individual field node bonuses: first = 50%, rest = 40%
                                const fieldPercent = farmFieldCount === 1 ? 50 : 40;
                                farmFieldNodes.push({ percent: fieldPercent });
                            }
                        }
                    }
                }
                
                // Farm field bonuses: first field = 50%, subsequent fields = 40% each
                if (farmFieldCount > 0) {
                    const firstFieldBonus = baseProduction * 0.5; // First field: +50%
                    const subsequentFieldsBonus = baseProduction * 0.4 * (farmFieldCount - 1); // Rest: +40% each
                    bonus = firstFieldBonus + subsequentFieldsBonus;
                }
                
                // Also count lake nodes for additional bonus (separate from farm fields)
                // Lake nodes give a percentage bonus on the value after farm fields
                const lakeNodes = this.gameEngine.countAround(LAKE, i, j);
                if (lakeNodes > 0) {
                    // Each lake node gives 45% bonus
                    const afterFields = baseProduction + bonus;
                    const lakeBonusPercent = 45; // 45% per lake node
                    const lakeBonusValue = afterFields * (lakeBonusPercent / 100) * lakeNodes;
                    bonus = afterFields + lakeBonusValue;
                }
            } else {
                // For other resources, use standard resource node bonuses
                // Node bonuses are percentages of base: 45% per node
                const resourceNodes = this.gameEngine.countAround(resourceType, i, j);
                const nodeBonusPercent = 45; // Each node gives 45% of base
                bonus = baseProduction * (nodeBonusPercent / 100) * resourceNodes;
            }
            
            // Start with base + node bonus
            let currentValue = baseProduction + bonus;
            
            // Apply refinery bonuses (based on building level)
            // Count refineries: SAWMILL for WOOD, STONEMASON for STONE, FOUNDRY for IRON, MILL for FOOD
            const refineryTile = resourceType === WOOD ? SAWMILL :
                                resourceType === STONE ? STONEMASON :
                                resourceType === IRON ? FOUNDRY : MILL;
            const hasRefiner = this.gameEngine.countAround(refineryTile, i, j) > 0;
            const refinerBonus = getRefineryBonus(level);
            // Refiner bonus: apply as percentage of current value (additive)
            if (hasRefiner) {
                currentValue = currentValue + (currentValue * refinerBonus);
            }
            
            // Apply casa bonuses (based on building level)
            const nCasas = this.gameEngine.countAround(CASA, i, j);
            const casaBonus = getCottageBonus(level);
            // Casa bonus: apply as percentage of current value (additive)
            if (nCasas > 0) {
                currentValue = currentValue + (currentValue * casaBonus * nCasas);
            }
            
            // Final value
            const value = currentValue;
            
            if (resourceType === WOOD) stats.production.wood = Math.round(value);
            else if (resourceType === STONE) stats.production.stone = Math.round(value);
            else if (resourceType === IRON) stats.production.iron = Math.round(value);
            else if (resourceType === FOOD) {
                stats.production.food = Math.round(value);
                // Store farm field nodes for display
                stats.farmFieldNodes = farmFieldNodes;
            }
        } else if (tile === MANOR && baseProduction > 0) {
            // Gold production with bonuses (matching GameEngine logic, using level-based bonuses)
            const wharfs = this.gameEngine.countAround(WHARF, i, j);
            const bazaars = this.gameEngine.countAround(BAZAAR, i, j);
            const wharfBonus = getHarborBonus(level) * wharfs;
            const bazaarBonus = getMarketBonus(level) * bazaars;
            const value = baseProduction * (1.0 + harborBonus + marketBonus);
            stats.production.gold = Math.round(value);
        }
        
        // Get actual production values from game engine (for comparison/verification)
        // These should match our calculated values since they use the same level
        const woodProd = this.gameEngine.getResourceValue(WOOD, i, j);
        const stoneProd = this.gameEngine.getResourceValue(STONE, i, j);
        const ironProd = this.gameEngine.getResourceValue(IRON, i, j);
        const foodProd = this.gameEngine.getResourceValue(FOOD, i, j);
        const goldProd = this.gameEngine.getGoldValue(i, j);
        
        // Always use calculated values (they're more accurate and use the correct level)
        // Only fall back to game engine values if calculation failed
        if (stats.production.wood === undefined || stats.production.wood === null) {
            if (woodProd > 0) stats.production.wood = woodProd;
        }
        if (stats.production.stone === undefined || stats.production.stone === null) {
            if (stoneProd > 0) stats.production.stone = stoneProd;
        }
        if (stats.production.iron === undefined || stats.production.iron === null) {
            if (ironProd > 0) stats.production.iron = ironProd;
        }
        if (stats.production.food === undefined || stats.production.food === null) {
            if (foodProd > 0) stats.production.food = foodProd;
        }
        if (stats.production.gold === undefined || stats.production.gold === null) {
            if (goldProd > 0) stats.production.gold = goldProd;
        }
        
        // Calculate detailed bonuses breakdown
        if (tile === CUTTER || tile === CUTTER_OLD || tile === QUARRY || tile === QUARRY_OLD || 
            tile === MINE || tile === MINE_OLD || tile === FARM || tile === FARM_OLD) {
            const resourceType = tile === CUTTER || tile === CUTTER_OLD ? WOOD :
                                tile === QUARRY || tile === QUARRY_OLD ? STONE :
                                tile === MINE || tile === MINE_OLD ? IRON : FOOD;
            
            // Special handling for farms: count farm field nodes (OPEN spots)
            if (resourceType === FOOD) {
                // Count farm field nodes (OPEN spots adjacent to this farm)
                let farmFieldCount = 0;
                const farmFieldNodes = [];
                for (let di = -1; di <= 1; di++) {
                    for (let dj = -1; dj <= 1; dj++) {
                        if (di === 0 && dj === 0) continue; // Skip self
                        const ni = i + di;
                        const nj = j + dj;
                        if (ni >= 0 && ni < 21 && nj >= 0 && nj < 21) {
                            const neighborTile = this.gameEngine.map[ni][nj];
                            // Farm field node: OPEN spot (empty land) adjacent to farm
                            if (neighborTile === OPEN && !this.gameEngine.isLocked(ni, nj)) {
                                farmFieldCount++;
                                // First field = 50%, subsequent = 40%
                                const fieldPercent = farmFieldCount === 1 ? 50 : 40;
                                farmFieldNodes.push({ percent: fieldPercent });
                            }
                        }
                    }
                }
                
                if (farmFieldCount > 0) {
                    stats.bonuses.farmFieldNodes = farmFieldNodes;
                }
                
                // Also count lake nodes separately (they give additional bonus)
                const lakeNodes = this.gameEngine.countAround(LAKE, i, j);
                if (lakeNodes > 0) {
                    // Lake nodes give 45% bonus per node on value after farm fields
                    const afterFields = baseProduction + (baseProduction * 0.5) + (baseProduction * 0.4 * (farmFieldCount - 1));
                    const lakeBonusPercent = 45 * lakeNodes; // 45% per lake node
                    const lakeBonusValue = afterFields * (lakeBonusPercent / 100);
                    stats.bonuses.lakeNodes = { count: lakeNodes, bonus: lakeBonusValue, percent: lakeBonusPercent };
                }
            } else {
                // For other resources, count standard resource nodes
                // Resource node bonuses: ALL nodes give 45% each (135 at base 300)
                // So: 0 nodes = 0, 1 node = 135, 2 nodes = 270, 3 nodes = 405, etc.
                
                // Count matching resource nodes (the ones that give bonus to this building)
                const matchingResourceNodes = this.gameEngine.countAround(resourceType, i, j);
                
                if (matchingResourceNodes > 0) {
                    const resourceNodeArray = [];
                    const nodeBonusPercent = 45; // All nodes give 45% of base
                    
                    // Calculate bonus for each node (all are the same, percentage of base)
                    for (let nodeIdx = 0; nodeIdx < matchingResourceNodes; nodeIdx++) {
                        const nodeBonus = baseProduction * (nodeBonusPercent / 100);
                        resourceNodeArray.push({ 
                            bonus: nodeBonus, 
                            percent: nodeBonusPercent 
                        });
                    }
                    
                    // Store as array (like farmFieldNodes) for individual display
                    if (resourceType === WOOD) {
                        stats.bonuses.woodNodes = resourceNodeArray;
                    } else if (resourceType === STONE) {
                        stats.bonuses.stoneNodes = resourceNodeArray;
                    } else if (resourceType === IRON) {
                        stats.bonuses.ironNodes = resourceNodeArray;
                    }
                }
            }
            
            // Count refineries and calculate bonus %
            const refineryTile = resourceType === WOOD ? SAWMILL :
                                resourceType === STONE ? STONEMASON :
                                resourceType === IRON ? FOUNDRY : MILL;
            const refineries = this.gameEngine.countAround(refineryTile, i, j);
            const hasRefiner = refineries > 0;
            const refinerBonus = getRefineryBonus(level);
            const refinerBonusPercent = hasRefiner ? refinerBonus * 100 : 0;
            
            // Get the refinery name (plural form)
            let refineryName = 'Refineries';
            if (resourceType === WOOD) {
                refineryName = 'Sawmills';
            } else if (resourceType === STONE) {
                refineryName = 'Mason\'s Workshops';
            } else if (resourceType === IRON) {
                refineryName = 'Forges';
            } else if (resourceType === FOOD) {
                refineryName = 'Mills';
            }
            
            if (hasRefiner) {
                stats.bonuses.refineries = { count: refineries, bonusPercent: refinerBonusPercent, name: refineryName };
            }
            
            // Count casas and calculate bonus %
            const casas = this.gameEngine.countAround(CASA, i, j);
            const casaBonus = getCottageBonus(level);
            const casaBonusPercent = casaBonus * 100;
            
            if (casas > 0) {
                stats.bonuses.casas = { count: casas, bonusPercent: casaBonusPercent, totalPercent: casaBonusPercent * casas };
            }
            
            // Calculate resourceNodeBonus and resourceNodeBonusPercent for production breakdown
            let resourceNodeBonus = 0;
            let resourceNodeBonusPercent = 0;
            
            if (resourceType === FOOD) {
                // For farms, calculate farm field bonus
                const farmFieldCount = stats.bonuses.farmFieldNodes ? stats.bonuses.farmFieldNodes.length : 0;
                if (farmFieldCount > 0) {
                    resourceNodeBonus = (baseProduction * 0.5) + (baseProduction * 0.4 * (farmFieldCount - 1));
                    resourceNodeBonusPercent = 50 + (40 * (farmFieldCount - 1)); // First = 50%, rest = 40% each
                }
                // Add lake node bonus if present
                if (stats.bonuses.lakeNodes) {
                    resourceNodeBonus += stats.bonuses.lakeNodes.bonus || 0;
                    resourceNodeBonusPercent += stats.bonuses.lakeNodes.percent || 0;
                }
            } else {
                // Sum up bonuses from individual nodes
                const nodeArray = resourceType === WOOD ? stats.bonuses.woodNodes :
                                 resourceType === STONE ? stats.bonuses.stoneNodes :
                                 resourceType === IRON ? stats.bonuses.ironNodes : null;
                if (nodeArray && nodeArray.length > 0) {
                    resourceNodeBonus = nodeArray.reduce((sum, node) => sum + node.bonus, 0);
                    resourceNodeBonusPercent = nodeArray.reduce((sum, node) => sum + node.percent, 0);
                }
            }
            
            // Store production breakdown for display
            stats.productionBreakdown = {
                base: baseProduction,
                resourceNodeBonus: resourceNodeBonus,
                resourceNodeBonusPercent: resourceNodeBonusPercent,
                refinerBonusPercent: refinerBonusPercent,
                casaBonusPercent: casaBonusPercent,
                casaCount: casas,
                totalCasaBonusPercent: casaBonusPercent * casas,
                total: stats.production[resourceType === WOOD ? 'wood' : resourceType === STONE ? 'stone' : resourceType === IRON ? 'iron' : 'food'] || 0
            };
        }
        
        // Gold building bonuses
        if (tile === MANOR) {
            const wharfs = this.gameEngine.countAround(WHARF, i, j);
            const bazaars = this.gameEngine.countAround(BAZAAR, i, j);
            
            if (wharfs > 0) {
                stats.bonuses.wharfs = wharfs;
            }
            if (bazaars > 0) {
                stats.bonuses.bazaars = bazaars;
            }
        }
        
        // Storage buildings
        if (tile === CELLARIUM) {
            stats.storage = getGranaryStorage(level);
        } else if (tile === CACHE) {
            stats.storage = getVaultStorage(level);
        } else if (tile === TOWN_HALL) {
            stats.storage = getKeepStorage(level);
            stats.other.buildingSlots = getKeepBuildingSlots(level);
            // Keep wood production is constant 300 per hour at all levels
            stats.production.wood = 300;
        }
        
        // Refinery buildings - show bonus info
        if (tile === SAWMILL || tile === STONEMASON || tile === FOUNDRY || tile === MILL) {
            const refinerBonus = getRefineryBonus(level);
            stats.other.refineryBonus = `${Math.round(refinerBonus * 100)}%`;
        }
        
        // Casa buildings - show bonus info
        if (tile === CASA) {
            const casaBonus = getCottageBonus(level);
            stats.other.productionBonus = `${Math.round(casaBonus * 100)}%`;
            // Construction speed bonus from buildings.md: +4%, +10%, +16%, +25%, +35%, +46%, +58%, +71%, +85%, +100%
            const constructionSpeedBonus = [4, 10, 16, 25, 35, 46, 58, 71, 85, 100][level - 1] || 100;
            stats.other.constructionSpeed = `+${constructionSpeedBonus}%`;
        }
        
        // Wharf/Bazaar buildings - show bonus info
        if (tile === WHARF) {
            const wharfBonus = getHarborBonus(level);
            stats.other.taxBonus = `${Math.round(wharfBonus * 100)}%`;
        }
        if (tile === BAZAAR) {
            const bazaarBonus = getMarketBonus(level);
            stats.other.taxBonus = `${Math.round(bazaarBonus * 100)}%`;
        }
        
        // Military buildings - enlistment speeds and army size
        // Enlistment speed pattern: +5%, +10%, +16%, +24%, +35%, +50%, +68%, +90%, +115%, +150%
        const enlistmentSpeeds = [5, 10, 16, 24, 35, 50, 68, 90, 115, 150];
        
        if (tile === CITY_GUARD_HOUSE) {
            stats.other.enlistmentSpeed = `+${enlistmentSpeeds[level - 1] || 150}%`;
            stats.other.unitType = 'Watchman';
        }
        
        // Barracks enlistment speed bonuses by level: +1%, +2%, +4%, +6%, +8%, +10%, +13%, +16%, +20%, +25%
        const barracksEnlistmentBonuses = [1, 2, 4, 6, 8, 10, 13, 16, 20, 25];
        const barracksLevel = this.gameEngine.buildingLevel || 10;
        const barracksBonusPercent = barracksEnlistmentBonuses[barracksLevel - 1] || 25;
        
        // Calculate bonuses from adjacent barracks for military buildings
        const militaryBuildings = [TRAINING_GROUND, STABLES, MOONGLOW, SHIPYARD, WORKSHOP, TEMPLE];
        if (militaryBuildings.includes(tile)) {
            const adjacentBarracks = this.gameEngine.countAround(BARRACKS, i, j);
            if (adjacentBarracks > 0) {
                // Create array of barracks bonuses (one per adjacent barracks)
                const barracksBonuses = [];
                for (let idx = 0; idx < adjacentBarracks; idx++) {
                    barracksBonuses.push({ 
                        level: barracksLevel, 
                        bonus: barracksBonusPercent 
                    });
                }
                stats.bonuses.barracks = barracksBonuses;
            }
        }
        
        if (tile === TRAINING_GROUND) {
            stats.other.enlistmentSpeed = `+${enlistmentSpeeds[level - 1] || 150}%`;
            stats.other.unitType = 'Infantry';
        }
        
        if (tile === STABLES) {
            stats.other.enlistmentSpeed = `+${enlistmentSpeeds[level - 1] || 150}%`;
            stats.other.unitType = 'Cavalry';
        }
        
        if (tile === MOONGLOW) {
            stats.other.enlistmentSpeed = `+${enlistmentSpeeds[level - 1] || 150}%`;
            stats.other.unitType = 'Mystic';
        }
        
        if (tile === SHIPYARD) {
            stats.other.enlistmentSpeed = `+${enlistmentSpeeds[level - 1] || 150}%`;
            stats.other.unitType = 'Naval';
        }
        
        if (tile === WORKSHOP) {
            stats.other.enlistmentSpeed = `+${enlistmentSpeeds[level - 1] || 150}%`;
            stats.other.unitType = 'Artillery';
        }
        
        // Barracks - Army Size and Enlistment Speed Bonus
        if (tile === BARRACKS) {
            const armySizes = [10, 30, 60, 100, 160, 240, 350, 500, 700, 1000];
            const enlistmentBonuses = [1, 2, 4, 6, 8, 10, 13, 16, 20, 25];
            stats.other.armySize = armySizes[level - 1] || 1000;
            stats.other.enlistmentBonus = `+${enlistmentBonuses[level - 1] || 25}%`;
            
            // Calculate bonuses TO adjacent military buildings
            const barracksBonusPercent = enlistmentBonuses[level - 1] || 25;
            const militaryBuildings = [
                { tile: TRAINING_GROUND, name: 'Training Grounds' },
                { tile: STABLES, name: 'Stable' },
                { tile: MOONGLOW, name: 'Mage Tower' },
                { tile: SHIPYARD, name: 'Shipyard' },
                { tile: WORKSHOP, name: 'Siege Workshop' },
                { tile: TEMPLE, name: 'War College' }
            ];
            
            const bonusesTo = [];
            for (const military of militaryBuildings) {
                const count = this.gameEngine.countAround(military.tile, i, j);
                if (count > 0) {
                    bonusesTo.push({
                        name: military.name,
                        level: this.gameEngine.buildingLevel || 10,
                        bonus: barracksBonusPercent,
                        count: count
                    });
                }
            }
            
            if (bonusesTo.length > 0) {
                stats.bonuses.bonusesTo = bonusesTo;
            }
        }
        
        // Castle - Command Count and Army Size
        if (tile === CASTLE) {
            const commandCounts = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
            const armySizeBonuses = [20, 40, 60, 80, 100, 130, 160, 200, 250, 300];
            stats.other.commandCount = `+${commandCounts[level - 1] || 10}`;
            stats.other.armySizeBonus = `+${armySizeBonuses[level - 1] || 300}%`;
        }
        
        // Calculate costs for ALL buildings (not just production ones)
        const buildingTypeKey = this.getBuildingTypeKey(tile);
        if (buildingTypeKey) {
            // Cost for current level
            stats.costs.currentLevel = getBuildingCost(buildingTypeKey, level);
            
            // Cost for next level (if not max level)
            if (level < 10) {
                stats.costs.nextLevel = getBuildingCost(buildingTypeKey, level + 1);
            } else {
                stats.costs.nextLevel = null;
            }
            
            // Total cost to current level
            stats.costs.totalToCurrent = getTotalCostToLevel(buildingTypeKey, level);
            
            // Total cost to level 10
            stats.costs.totalToLevel10 = getTotalCostToLevel(buildingTypeKey, 10);
        }
        
        return stats;
    }
    
    /**
     * Get building type key for cost lookup
     */
    getBuildingTypeKey(tile) {
        const keyMap = {
            [CUTTER]: 'CUTTER',
            [CUTTER_OLD]: 'CUTTER',
            [QUARRY]: 'QUARRY',
            [QUARRY_OLD]: 'QUARRY',
            [MINE]: 'MINE',
            [MINE_OLD]: 'MINE',
            [FARM]: 'FARM',
            [FARM_OLD]: 'FARM',
            [MANOR]: 'MANOR',
            [CASA]: 'CASA',
            [CELLARIUM]: 'CELLARIUM',
            [CACHE]: 'CACHE',
            [WHARF]: 'WHARF',
            [BAZAAR]: 'BAZAAR',
            [TOWN_HALL]: 'TOWN_HALL',
            [WALL]: 'WALL',
            [SAWMILL]: 'SAWMILL',
            [STONEMASON]: 'STONEMASON',
            [FOUNDRY]: 'FOUNDRY',
            [MILL]: 'MILL',
            [BARRACKS]: 'BARRACKS',
            [CITY_GUARD_HOUSE]: 'CITY_GUARD_HOUSE',
            [TRAINING_GROUND]: 'TRAINING_GROUND',
            [STABLES]: 'STABLES',
            [MOONGLOW]: 'MOONGLOW',
            [SHIPYARD]: 'SHIPYARD',
            [WORKSHOP]: 'WORKSHOP',
            [CASTLE]: 'CASTLE'
        };
        return keyMap[tile] || null;
    }
    
    /**
     * Get building name
     */
    getBuildingName(tile) {
        const names = {
            [TOWN_HALL]: 'Basilica',
            [WALL]: 'City Wall',
            [DEEP_WATER]: 'Deep Water',
            [WATER]: 'Water Spot',
            [CUTTER]: 'Forester\'s Hut',
            [SAWMILL]: 'Sawmill',
            [CUTTER_OLD]: 'Forester\'s Hut (Old)',
            [QUARRY]: 'Stone Mine',
            [STONEMASON]: 'Mason\'s Hut',
            [QUARRY_OLD]: 'Stone Mine (Old)',
            [MINE]: 'Iron Mine',
            [FOUNDRY]: 'Smelter',
            [MINE_OLD]: 'Iron Mine (Old)',
            [FARM]: 'Farm Estate',
            [MILL]: 'Grain Mill',
            [FARM_OLD]: 'Farm Estate (Old)',
            [MANOR]: 'Manor',
            [BAZAAR]: 'Bazaar',
            [WHARF]: 'Wharf',
            [CASA]: 'Casa',
            [CELLARIUM]: 'Cellarium',
            [CACHE]: 'Cache',
            [BARRACKS]: 'Barracks',
            [CASTLE]: 'Castle',
            [CITY_GUARD_HOUSE]: 'Guardhouse',
            [SHIPYARD]: 'Shipyard',
            [MOONGLOW]: 'Sorcerer\'s Tower',
            [STABLES]: 'Stables',
            [TEMPLE]: 'Temple',
            [TRAINING_GROUND]: 'Training Arena',
            [WORKSHOP]: 'Blacksmith',
        };
        
        return names[tile] || `Building 0x${tile.toString(16)}`;
    }
    
    /**
     * Get building description from buildings.md
     */
    getBuildingDescription(tile) {
        const descriptions = {
            [TOWN_HALL]: 'At the centre of your city is the Basilica, which stores some of your resources and produces wood. Increase the number of buildings you can add to your city by upgrading the Basilica. It cannot be totally destroyed, and only one Basilica can be built per city.',
            [CUTTER]: 'Forester\'s Huts increase your wood production. Place Forester\'s Huts next to forest resource nodes, Casas or a Sawmill to increase their output.',
            [CUTTER_OLD]: 'Forester\'s Huts increase your wood production. Place Forester\'s Huts next to forest resource nodes, Casas or a Sawmill to increase their output.',
            [CASA]: 'Casas provide housing for peasants, which increase the construction speed of all your city\'s buildings. Casas also increase the productivity of any resource production buildings adjacent to them.',
            [CELLARIUM]: 'Cellariums store your resources, increasing your city\'s maximum storage capacity. Increase the amount a Cellarium can hold by placing it next to Sawmills, Mason\'s Huts, Grain Mills or Smelters.',
            [WALL]: 'Your city is surrounded by a City Wall, which cannot be completely destroyed. It increases your land units\' defence against attack.',
            [QUARRY]: 'Stone Mines increase your stone production. Place Stone Mines next to stone resource nodes, Casas or a Mason\'s Hut to increase their output.',
            [QUARRY_OLD]: 'Stone Mines increase your stone production. Place Stone Mines next to stone resource nodes, Casas or a Mason\'s Hut to increase their output.',
            [CACHE]: 'Caches allow you to store resources safely, and they cannot be plundered, nor counted by Scouts. Increase the capacity of your Cache by placing it next to forest resource nodes.',
            [FARM]: 'Farms grow crops in adjacent fields, producing food for your troops. Place Farm Estates next to lake resource nodes, Casas or a Grain Mill to increase their output.',
            [FARM_OLD]: 'Farms grow crops in adjacent fields, producing food for your troops. Place Farm Estates next to lake resource nodes, Casas or a Grain Mill to increase their output.',
            [MINE]: 'Iron Mines increase your iron production. Place Iron Mines next to iron resource nodes, Casas or a Smelter to increase their output.',
            [MINE_OLD]: 'Iron Mines increase your iron production. Place Iron Mines next to iron resource nodes, Casas or a Smelter to increase their output.',
            [BARRACKS]: 'The Barracks houses your troops and increases your city\'s army size. It also boosts the enlistment speed of adjacent military buildings.',
            [CITY_GUARD_HOUSE]: 'Building a Guardhouse will allow you to enlist Guard Units. Upgrade your Guardhouse to increase its Guard enlistment rate. You can enlist as many Guards as you can feed! Guard numbers and enlistment speeds are not dependent on Barracks.',
            [MANOR]: 'Building a Manor allows you to collect taxes in the form of Gold from your city. Place a Manor next to Bazaars and Wharfs to increase Gold production.',
            [BAZAAR]: 'The Bazaar allows you to trade with other players. Use the Bazaar to arrange for the transport of resources to other cities by land, via carts. You can also place a Manor next to a Bazaar to boost the Manor\'s gold production.',
            [SAWMILL]: 'Sawmills boost the output of bordering Forester\'s Huts, and increase the wood storage capacity of any bordering Cellariums.',
            [STABLES]: 'Building a Stable will allow you to enlist Cavalry units. Upgrade your Stable to increase its unit enlistment rate. Place a Stable next to a Barracks to further boost its enlistment rate.',
            [STONEMASON]: 'Mason\'s Huts boost the output of bordering Stone Mines, and increase the stone storage capacity of any bordering Cellariums.',
            [MILL]: 'Grain Mills boost the output of bordering Farm Estates and increase the food storage capacity of any bordering Cellariums.',
            [FOUNDRY]: 'Smelters boost the output of bordering Iron Mines and increase the iron storage capacity of any bordering Cellariums.',
            [WHARF]: 'Wharfs create Trader Ships that move resources between cities and allow you to trade with other players by sea. Place a Manor next to a Wharf to boost the Manor\'s gold production.',
            [SHIPYARD]: 'Building a Shipyard will allow you to enlist Naval units. Upgrade your Shipyard to increase its unit enlistment rate. Place a Shipyard next to a Barracks to further boost its enlistment rate.',
            [CASTLE]: 'Building a Castle allows you to plunder, scout, assault and siege other cities - but it also makes your own city vulnerable to attacks from other players! Building a Castle will exponentially increase the maximum army size of your city, but is an irreversible action.',
            [TEMPLE]: 'Building a Temple gives your alliance Loyalty to one of the eight gods of Aschendal. A Temple can be built in a Castled city which is currently Blessed by one of the eight gods. Successive upgrades of existing Temples will require certain amounts of existing alliance Loyalty. Eight level 10 Temples, one built to honor each of the eight gods, are required in order for an alliance to achieve victory and become Champions!',
            [TRAINING_GROUND]: 'Building a Training Arena will allow you to enlist Infantry units. Upgrade your Training Arena to increase its unit enlistment rate. Place a Training Arena next to a Barracks to further boost its enlistment rate.',
            [MOONGLOW]: 'Building a Sorcerer\'s Tower will allow you to enlist Mystic units. Upgrade your Sorcerer\'s Tower to increase its unit enlistment rate. Place a Sorcerer\'s Tower next to a Barracks to further boost its enlistment rate. A level 10 Sorcerer\'s Tower allows you to Refine resources.',
            [WORKSHOP]: 'Building a Blacksmith will allow you to enlist Artillery. Upgrade your Blacksmith to increase its unit enlistment rate. Place a Blacksmith next to a Barracks to further boost its enlistment rate.',
        };
        
        return descriptions[tile] || null;
    }
    
    /**
     * Format number
     */
    formatNumber(num) {
        return Math.round(num).toLocaleString();
    }
    
    /**
     * Render production breakdown showing base + all bonuses
     */
    renderProductionBreakdown(resourceName, total, breakdown) {
        if (!breakdown) {
            return `<div class="stat-row">${resourceName}: ${this.formatNumber(total)}/hr</div>`;
        }
        
        const parts = [];
        // Base production
        parts.push(`Base: ${this.formatNumber(breakdown.base)}`);
        
        // Resource node bonus (flat addition)
        if (breakdown.resourceNodeBonus > 0) {
            parts.push(`+${this.formatNumber(breakdown.resourceNodeBonus)} (nodes: +${Math.round(breakdown.resourceNodeBonusPercent)}%)`);
        }
        
        // Calculate intermediate value after resource nodes
        const afterNodes = breakdown.base + breakdown.resourceNodeBonus;
        
        // Refinery bonus (multiplier)
        if (breakdown.refinerBonusPercent > 0) {
            const afterRefinery = afterNodes * (1 + breakdown.refinerBonusPercent / 100);
            parts.push(`×${(1 + breakdown.refinerBonusPercent / 100).toFixed(2)} (refinery: +${Math.round(breakdown.refinerBonusPercent)}%) = ${this.formatNumber(Math.round(afterRefinery))}`);
        }
        
        // Casa bonus (multiplier)
        if (breakdown.casaCount > 0) {
            const beforeCasas = breakdown.refinerBonusPercent > 0 ? afterNodes * (1 + breakdown.refinerBonusPercent / 100) : afterNodes;
            const casaMultiplier = 1 + (breakdown.totalCasaBonusPercent / 100);
            parts.push(`×${casaMultiplier.toFixed(2)} (casas: +${Math.round(breakdown.totalCasaBonusPercent)}%) = ${this.formatNumber(total)}`);
        } else if (breakdown.refinerBonusPercent === 0) {
            // No refinery or cabin bonuses, just show total
            parts.push(`= ${this.formatNumber(total)}`);
        }
        
        return `
            <div class="stat-row" style="margin-bottom: 4px;">
                <div><strong>${resourceName}: ${this.formatNumber(total)}/hr</strong></div>
                <div style="font-size: 0.85em; color: var(--text-color-secondary); margin-left: 8px; margin-top: 2px;">
                    ${parts.join(' → ')}
                </div>
            </div>
        `;
    }
    
    /**
     * Render the component
     */
    render() {
        if (!this.currentTile || !this.gameEngine) {
            this.container.innerHTML = `
                <div class="building-stats">
                    <h4>Building Stats</h4>
                    <p class="no-stats">Hover over a building to see stats</p>
                </div>
            `;
            return;
        }
        
        const { i, j } = this.currentTile;
        const tile = this.gameEngine?.map?.[i]?.[j];
        
        // Check if it's an open space
        if (tile === OPEN) {
            this.container.innerHTML = `
                <div class="building-stats">
                    <h4>Open Space</h4>
                    <div class="stats-info">
                        <div class="building-name" style="display: flex; align-items: center; gap: 8px;">
                            <img src="${getImagePath('ui/icon_build_slots.png')}" 
                                 alt="Open Space" 
                                 title="Open Space"
                                 class="building-stats-icon"
                                 style="width: 32px; height: 32px; object-fit: contain; flex-shrink: 0;"
                                 onerror="this.style.display='none'" />
                            <span>Open Space</span>
                        </div>
                        <div class="building-description" style="margin-top: 8px; padding: 10px; background: rgba(199, 168, 107, 0.1); border-radius: 4px; border: 1px solid rgba(199, 168, 107, 0.3);">
                            <p style="margin: 0; font-style: italic; color: var(--accent-color);">This spot is open! Think of the possibilities!</p>
                        </div>
                    </div>
                </div>
            `;
            return;
        }
        
        // Check if it's a resource node (not a building)
        if (tile === WOOD || tile === STONE || tile === IRON || tile === LAKE || tile === FOOD) {
            this.renderResourceNodeInfo(tile, i, j);
            return;
        }
        
        const stats = this.getBuildingStats(i, j);
        
        if (!stats) {
            this.container.innerHTML = `
                <div class="building-stats">
                    <h4>Building Stats</h4>
                    <p class="no-stats">No building at this position</p>
                </div>
            `;
            return;
        }
        
        const buildingName = this.getBuildingName(stats.building);
        const buildingDescription = this.getBuildingDescription(stats.building);
        const hasProduction = Object.keys(stats.production).length > 0;
        const hasBonuses = Object.keys(stats.bonuses).length > 0;
        const hasOther = stats.other && Object.keys(stats.other).length > 0;
        const hasCosts = stats.costs && Object.keys(stats.costs).length > 0;
        
        // Get building icon
        const imageKey = getTileImageKey(stats.building);
        const buildingKey = this.getBuildingKey(stats.building);
        
        // Declare imageSrc variable
        let imageSrc = '';
        
        // If no building key (e.g., water tiles), don't try to load an image
        // The image will just not be displayed, which is fine for water tiles
        if (!buildingKey) {
            // For water tiles, we'll just skip the image but still show stats if available
            imageSrc = '';
        } else {
            // Check if this is a military or resource building in the sprite sheet
            const isMilbuilding = buildingKey && MILBUILDINGS_SPRITE_MAP[buildingKey];
            const isResbuilding = buildingKey && RESBUILDINGS_SPRITE_MAP[buildingKey];
            
            // Get image source (async for sprite sheet, sync for regular images)
            if (isMilbuilding) {
                // Use military buildings sprite sheet - will be loaded async
                getMilbuildingSprite(buildingKey).then(dataURL => {
                    if (dataURL) {
                        const img = this.container.querySelector('.building-stats-icon');
                        if (img) img.src = dataURL;
                    }
                }).catch(err => {
                    console.warn('Failed to load sprite from milbuildings.png:', err);
                });
                // Set placeholder initially
                imageSrc = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjMyIiBoZWlnaHQ9IjMyIiBmaWxsPSIjMzMzIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiM2NjYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5Mb2FkaW5nLi4uPC90ZXh0Pjwvc3ZnPg==';
            } else if (isResbuilding) {
                // Use resource buildings sprite sheet - will be loaded async
                getResbuildingSprite(buildingKey).then(dataURL => {
                    if (dataURL) {
                        const img = this.container.querySelector('.building-stats-icon');
                        if (img) img.src = dataURL;
                    }
                }).catch(err => {
                    console.warn('Failed to load sprite from resbuildings.png:', err);
                });
                // Set placeholder initially
                imageSrc = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjMyIiBoZWlnaHQ9IjMyIiBmaWxsPSIjMzMzIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiM2NjYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5Mb2FkaW5nLi4uPC90ZXh0Pjwvc3ZnPg==';
            } else {
                const filename = this.getImageFilename(imageKey || buildingKey);
                // Determine if it should use UI folder (resource icons, UI icons like icon_build_slots, or wall)
                const isUI = buildingKey && (
                    buildingKey.startsWith('resource_') || 
                    buildingKey.startsWith('icon_resource_') || 
                    buildingKey.startsWith('icon_') ||
                    buildingKey === 'building_wall'
                );
                const imageBase = getImageBasePath();
                imageSrc = isUI 
                    ? `${imageBase}/ui/${filename}.png`
                    : `${imageBase}/buildings/${filename}.png`;
            }
        }
        
        this.container.innerHTML = `
            <div class="building-stats">
                <h4>Building Stats</h4>
                <div class="stats-info">
                    <div class="building-name" style="display: flex; align-items: center; gap: 8px;">
                        ${imageSrc ? `<img src="${imageSrc}" 
                             alt="${buildingName}" 
                             title="${buildingName}"
                             class="building-stats-icon"
                             style="width: 32px; height: 32px; object-fit: contain; flex-shrink: 0;"
                             onerror="this.style.display='none'" />` : ''}
                        <span>${buildingName}${stats.level ? ` (Level ${stats.level})` : ''}</span>
                    </div>
                    ${buildingDescription ? `
                        <div class="building-description">${buildingDescription}</div>
                    ` : ''}
                    ${hasProduction ? `
                        <div class="stats-section">
                            <div class="stats-label">Production:</div>
                            ${stats.production.wood ? this.renderProductionBreakdown('Wood', stats.production.wood, stats.productionBreakdown) : ''}
                            ${stats.production.stone ? this.renderProductionBreakdown('Stone', stats.production.stone, stats.productionBreakdown) : ''}
                            ${stats.production.iron ? this.renderProductionBreakdown('Iron', stats.production.iron, stats.productionBreakdown) : ''}
                            ${stats.production.food ? this.renderProductionBreakdown('Food', stats.production.food, stats.productionBreakdown) : ''}
                            ${stats.production.gold ? `<div class="stat-row">Gold: ${this.formatNumber(stats.production.gold)}/hr</div>` : ''}
                        </div>
                    ` : ''}
                    ${hasBonuses ? `
                        <div class="stats-section">
                            <div class="stats-label">Bonuses:</div>
                            ${stats.bonuses.farmFieldNodes ? stats.bonuses.farmFieldNodes.map((field, idx) => 
                                `<div class="stat-row">Farm Field Node ${idx + 1}: +${field.percent}%</div>`
                            ).join('') : ''}
                            ${stats.bonuses.woodNodes ? stats.bonuses.woodNodes.map((node, idx) => 
                                `<div class="stat-row">Forest Node ${idx + 1}: +${this.formatNumber(node.bonus)} (+${Math.round(node.percent)}%)</div>`
                            ).join('') : ''}
                            ${stats.bonuses.stoneNodes ? stats.bonuses.stoneNodes.map((node, idx) => 
                                `<div class="stat-row">Stone Node ${idx + 1}: +${this.formatNumber(node.bonus)} (+${Math.round(node.percent)}%)</div>`
                            ).join('') : ''}
                            ${stats.bonuses.ironNodes ? stats.bonuses.ironNodes.map((node, idx) => 
                                `<div class="stat-row">Iron Node ${idx + 1}: +${this.formatNumber(node.bonus)} (+${Math.round(node.percent)}%)</div>`
                            ).join('') : ''}
                            ${stats.bonuses.lakeNodes ? `<div class="stat-row">Lake Nodes: ${stats.bonuses.lakeNodes.count} (+${this.formatNumber(stats.bonuses.lakeNodes.bonus)} / +${Math.round(stats.bonuses.lakeNodes.percent)}%)</div>` : ''}
                            ${stats.bonuses.refineries ? `<div class="stat-row">${stats.bonuses.refineries.name || 'Refineries'}: ${stats.bonuses.refineries.count} (+${Math.round(stats.bonuses.refineries.bonusPercent)}%)</div>` : ''}
                            ${stats.bonuses.casas ? `<div class="stat-row">Casas: ${stats.bonuses.casas.count} (+${Math.round(stats.bonuses.casas.bonusPercent)}% each = +${Math.round(stats.bonuses.casas.totalPercent)}% total)</div>` : ''}
                            ${stats.bonuses.barracks ? stats.bonuses.barracks.map((barracks, idx) => 
                                `<div class="stat-row">Bonus from lvl ${barracks.level} Barracks: ${barracks.bonus}%</div>`
                            ).join('') : ''}
                            ${stats.bonuses.bonusesTo ? stats.bonuses.bonusesTo.flatMap(military => {
                                const rows = [];
                                for (let i = 0; i < military.count; i++) {
                                    rows.push(`<div class="stat-row">Bonus to lvl ${military.level} ${military.name}: ${military.bonus}%</div>`);
                                }
                                return rows;
                            }).join('') : ''}
                            ${stats.bonuses.wharfs ? `<div class="stat-row">Wharfs: ${stats.bonuses.wharfs}</div>` : ''}
                            ${stats.bonuses.markets ? `<div class="stat-row">Markets: ${stats.bonuses.markets}</div>` : ''}
                        </div>
                    ` : ''}
                    ${stats.storage ? `
                        <div class="stats-section">
                            <div class="stats-label">Storage:</div>
                            <div class="stat-row">${this.formatNumber(stats.storage)}</div>
                        </div>
                    ` : ''}
                    ${hasOther ? `
                        <div class="stats-section">
                            <div class="stats-label">Properties:</div>
                            ${stats.other.refineryBonus ? `<div class="stat-row">Production Bonus: ${stats.other.refineryBonus}</div>` : ''}
                            ${stats.other.productionBonus ? `<div class="stat-row">Production Bonus: ${stats.other.productionBonus}</div>` : ''}
                            ${stats.other.constructionSpeed ? `<div class="stat-row">Construction Speed: ${stats.other.constructionSpeed}</div>` : ''}
                            ${stats.other.taxBonus ? `<div class="stat-row">Tax Bonus: ${stats.other.taxBonus}</div>` : ''}
                            ${stats.other.enlistmentSpeed ? `<div class="stat-row">${stats.other.unitType || 'Unit'} Enlistment Speed: ${stats.other.enlistmentSpeed}</div>` : ''}
                            ${stats.other.armySize ? `<div class="stat-row">Army Size: ${this.formatNumber(stats.other.armySize)}</div>` : ''}
                            ${stats.other.enlistmentBonus ? `<div class="stat-row">Enlistment Speed Bonus: ${stats.other.enlistmentBonus}</div>` : ''}
                            ${stats.other.commandCount ? `<div class="stat-row">Command Count: ${stats.other.commandCount}</div>` : ''}
                            ${stats.other.armySizeBonus ? `<div class="stat-row">Army Size Bonus: ${stats.other.armySizeBonus}</div>` : ''}
                            ${stats.other.buildingSlots ? `<div class="stat-row">Building Slots: ${stats.other.buildingSlots}</div>` : ''}
                        </div>
                    ` : ''}
                    ${hasCosts ? `
                        <div class="stats-section">
                            <div class="stats-label">Costs:</div>
                            <div class="costs-grid">
                                ${stats.costs.currentLevel ? `
                                    <div class="cost-item">
                                        <div class="cost-label">Current (${stats.level}):</div>
                                        <div class="cost-value">${this.formatCost(stats.costs.currentLevel)}</div>
                                        ${stats.costs.currentLevel.buildTime ? `
                                            <div class="cost-time">${this.formatBuildTimeWithIcon(stats.costs.currentLevel.buildTime)}</div>
                                        ` : ''}
                                    </div>
                                ` : ''}
                                ${stats.costs.nextLevel ? `
                                    <div class="cost-item">
                                        <div class="cost-label">Next (${stats.level + 1}):</div>
                                        <div class="cost-value">${this.formatCost(stats.costs.nextLevel)}</div>
                                        ${stats.costs.nextLevel.buildTime ? `
                                            <div class="cost-time">${this.formatBuildTimeWithIcon(stats.costs.nextLevel.buildTime)}</div>
                                        ` : ''}
                                    </div>
                                ` : ''}
                                ${stats.costs.totalToCurrent ? `
                                    <div class="cost-item">
                                        <div class="cost-label">Total to Current:</div>
                                        <div class="cost-value">${this.formatCost(stats.costs.totalToCurrent)}</div>
                                        ${stats.costs.totalToCurrent.buildTime ? `
                                            <div class="cost-time">${this.formatBuildTimeWithIcon(stats.costs.totalToCurrent.buildTime)}</div>
                                        ` : ''}
                                    </div>
                                ` : ''}
                                ${stats.costs.totalToLevel10 && stats.level < 10 ? `
                                    <div class="cost-item">
                                        <div class="cost-label">Total to Level 10:</div>
                                        <div class="cost-value">${this.formatCost(stats.costs.totalToLevel10)}</div>
                                        ${stats.costs.totalToLevel10.buildTime ? `
                                            <div class="cost-time">${this.formatBuildTimeWithIcon(stats.costs.totalToLevel10.buildTime)}</div>
                                        ` : ''}
                                    </div>
                                ` : ''}
                            </div>
                        </div>
                    ` : ''}
                </div>
            </div>
        `;
    }
    
    /**
     * Format cost for display with resource icons (without build time)
     */
    formatCost(cost) {
        const parts = [];
        const iconStyle = 'width: 14px; height: 14px; vertical-align: middle; margin-right: 2px;';
        
        if (cost.wood > 0) {
            parts.push(`<img src="${getImagePath('ui/icon_resource_wood.png')}" alt="Wood" style="${iconStyle}" onerror="this.style.display='none'">${this.formatNumber(cost.wood)}`);
        }
        if (cost.stone > 0) {
            parts.push(`<img src="${getImagePath('ui/icon_resource_stone.png')}" alt="Stone" style="${iconStyle}" onerror="this.style.display='none'">${this.formatNumber(cost.stone)}`);
        }
        
        return parts.length > 0 ? parts.join(' ') : 'Free';
    }
    
    /**
     * Format build time with clock icon, applying build speed multiplier
     */
    formatBuildTimeWithIcon(buildTime) {
        if (!buildTime || buildTime === 0) return '';
        
        // Apply build speed multiplier from casas
        // building_rate = cabin_tot + 1.0, so divide build time by this rate
        const buildRate = this.gameEngine?.building_rate || 1.0;
        const actualBuildTime = Math.max(1, Math.round(buildTime / buildRate));
        
        const iconStyle = 'width: 14px; height: 14px; vertical-align: middle; margin-right: 2px;';
        return `<img src="${getImagePath('ui/icon_clock_orange.png')}" alt="Build Time" style="${iconStyle}" onerror="this.style.display='none'">${formatBuildTime(actualBuildTime)}`;
    }
    
    /**
     * Render resource node information
     */
    renderResourceNodeInfo(tile, i, j) {
        let resourceName = '';
        let buildingName = '';
        let bonusPercent = 45; // Default bonus percentage
        let resourceIcon = '';
        
        if (tile === WOOD) {
            resourceName = 'Forest';
            buildingName = 'Foresters';
            resourceIcon = getImagePath('ui/icon_resource_wood.png');
        } else if (tile === STONE) {
            resourceName = 'Stone';
            buildingName = 'Stone Mines';
            resourceIcon = getImagePath('ui/icon_resource_stone.png');
        } else if (tile === IRON) {
            resourceName = 'Iron';
            buildingName = 'Iron Mines';
            resourceIcon = getImagePath('ui/icon_resource_iron.png');
        } else if (tile === LAKE || tile === FOOD) {
            resourceName = 'Lake';
            buildingName = 'Farms';
            resourceIcon = getImagePath('ui/icon_resource_grain.png');
        }
        
        this.container.innerHTML = `
            <div class="building-stats">
                <h4>Resource Node</h4>
                <div class="stats-info">
                    <div class="building-name" style="display: flex; align-items: center; gap: 8px;">
                        <img src="${resourceIcon}" 
                             alt="${resourceName} Node" 
                             title="${resourceName} Node"
                             class="building-stats-icon"
                             style="width: 32px; height: 32px; object-fit: contain; flex-shrink: 0;"
                             onerror="this.style.display='none'" />
                        <span>${resourceName} Node</span>
                    </div>
                    <div class="stats-section">
                        <div class="stats-label">Notes:</div>
                        <div class="stat-row" style="font-size: 10px; line-height: 1.4; margin-top: 4px;">
                            This is a ${resourceName} node. Add ${buildingName} near this node to get a ${bonusPercent}% production bonus from this node. Note that the bonus of each resource node stacks.
                        </div>
                    </div>
                    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-top: 4px;">
                        <div class="stats-section">
                            <div class="stats-label">Demolish Time:</div>
                            <div class="stat-row">1 Hour</div>
                        </div>
                        <div class="stats-section">
                            <div class="stats-label">Magic Powder:</div>
                            <div class="stat-row">Magic Powder Instantly Destroys 1 Resource Node</div>
                            <div class="stat-row" style="display: flex; align-items: center; gap: 4px;">
                                <img src="${getImagePath('ui/charisma.png')}" alt="Charisma" style="width: 16px; height: 16px; vertical-align: middle;" onerror="this.style.display='none'">
                                <span>50</span>
                            </div>
                            <div class="stat-row" style="display: flex; align-items: center; gap: 4px;">
                                <img src="${getImagePath('ui/Denari.png')}" alt="Denari" style="width: 16px; height: 16px; vertical-align: middle;" onerror="this.style.display='none'">
                                <span>249</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }
    
    /**
     * Get building key for image lookup
     */
    getBuildingKey(tile) {
        const keyMap = {
            [TOWN_HALL]: 'building_townhall',
            [CUTTER]: 'building_forester',
            [CUTTER_OLD]: 'building_forester',
            [SAWMILL]: 'building_sawmill',
            [QUARRY]: 'building_quarry',
            [QUARRY_OLD]: 'building_quarry',
            [STONEMASON]: 'building_stonemason',
            [MINE]: 'building_mine',
            [MINE_OLD]: 'building_mine',
            [FOUNDRY]: 'building_foundry',
            [FARM]: 'building_farm',
            [FARM_OLD]: 'building_farm',
            [MILL]: 'building_mill',
            [MANOR]: 'building_manor',
            [BAZAAR]: 'building_bazaar',
            [WHARF]: 'building_wharf',
            [CASA]: 'building_casa',
            [CELLARIUM]: 'building_cellarium',
            [CACHE]: 'building_cache',
            [BARRACKS]: 'building_barracks',
            [CASTLE]: 'building_castle',
            [CITY_GUARD_HOUSE]: 'building_guard_house',
            [SHIPYARD]: 'building_shipyard',
            [MOONGLOW]: 'building_moonglow',
            [STABLES]: 'building_stable',
            [TEMPLE]: 'building_temple',
            [TRAINING_GROUND]: 'building_training_ground',
            [WORKSHOP]: 'building_workshop',
            [WALL]: 'building_wall',
            [DEEP_WATER]: null, // Water tiles use background, no image
            [WATER]: null // Water tiles use background, no image
        };
        return keyMap[tile] || null;
    }
    
    /**
     * Get image filename from asset key
     */
    getImageFilename(key) {
        if (!key) return 'building_placeholder';
        
        // Map asset keys to actual filenames (matching AssetLoader paths)
        const filenameMap = {
            'building_townhall': 'basilica-fix-64',
            'building_forester': 'forester-v6-64',
            'building_sawmill': 'sawmill-64',
            'building_quarry': 'quarry-2-64',
            'building_stonemason': 'stonemasonery-64',
            'building_mine': 'iron-mine-64',
            'building_foundry': 'smelter-64',
            'building_farm': 'farm-v3-64',
            'building_mill': 'grain-mill-64-fix',
            'building_manor': 'villa-64',
            'building_bazaar': 'market-64',
            'building_wharf': 'ports-front-flip-64',
            'building_casa': 'basic_house-64',
            'building_cellarium': 'warehouse-v2-64',
            'building_cache': 'hideaway-64',
            'building_barracks': 'barracks-v2-64',
            'building_castle': 'castle-64-fix',
            'building_guard_house': 'guard\'s_house-v2-64',
            'building_shipyard': 'shipyard-front-flip-64',
            'building_moonglow': 'sorcerer\'s-tower-v2-64',
            'building_stable': 'stable-v2-fix-64',
            'building_temple': 'academy',
            'building_training_ground': 'training_arena-64',
            'building_workshop': 'blacksmith-v2-64',
            'building_wall': 'icon_build_slots' // Wall uses build slots icon from UI folder
        };
        
        return filenameMap[key] || key.replace('building_', '').replace(/_/g, '-') + '-64';
    }
}

